import React, { useState, useEffect, useCallback } from 'react';
import { FiRefreshCw, FiTrash2, FiFileText, FiDatabase } from 'react-icons/fi';
import { sopListDocuments, sopDeleteDocument } from '../../db/company';

function SkeletonRow() {
  return (
    <tr>
      {[1, 2, 3, 4].map(i => (
        <td key={i} style={{ padding: '14px 16px' }}>
          <div style={{
            height: 16, borderRadius: 4,
            background: 'linear-gradient(90deg, #1E2A4A 25%, #2A3A5A 50%, #1E2A4A 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
          }} />
        </td>
      ))}
    </tr>
  );
}

export default function DocumentLibrary() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await sopListDocuments();
      if (data?.error) throw new Error(data.error);
      setDocuments(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleDelete = async (sourceFileId, title) => {
    if (!window.confirm(`Delete "${title}" and all its chunks?`)) return;
    setDeleting(sourceFileId);
    try {
      const data = await sopDeleteDocument(sourceFileId);
      if (data?.error) throw new Error(data.error);
      showToast(`Deleted ${data.deleted} chunks from "${title}"`);
      setDocuments(prev => prev.filter(d => d.source_file_id !== sourceFileId));
    } catch (e) {
      showToast(`Failed to delete: ${e.message}`, 'error');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div style={{ padding: '0 0 40px 0' }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          padding: '12px 20px', borderRadius: 8,
          background: toast.type === 'error' ? '#DC2626' : '#059669',
          color: '#fff', fontSize: 14, fontWeight: 500,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          animation: 'fadeIn 0.2s ease',
        }}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 24,
      }}>
        <div>
          <h1 style={{ color: '#F1F5F9', fontSize: 24, fontWeight: 700, margin: 0 }}>
            Document Library
          </h1>
          <p style={{ color: '#64748B', fontSize: 14, margin: '4px 0 0' }}>
            All SOP documents currently stored in the vector database
          </p>
        </div>
        <button
          onClick={fetchDocuments}
          disabled={loading}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 18px', borderRadius: 8,
            background: '#1E3A5F', border: '1px solid #2A4A6A',
            color: '#3B82F6', fontSize: 14, fontWeight: 500,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
          }}
        >
          <FiRefreshCw size={16} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: 8, marginBottom: 16,
          background: '#1C1917', border: '1px solid #DC2626', color: '#FCA5A5', fontSize: 14,
        }}>
          Failed to load documents: {error}
        </div>
      )}

      {/* Table */}
      <div style={{
        background: '#0F1A2E', borderRadius: 12,
        border: '1px solid #1E2A4A', overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #1E2A4A' }}>
              <th style={thStyle}>Document Title</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Chunks</th>
              <th style={thStyle}>Last Ingested</th>
              <th style={{ ...thStyle, textAlign: 'center', width: 80 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <>
                <SkeletonRow /><SkeletonRow /><SkeletonRow />
              </>
            ) : documents.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ padding: '60px 16px', textAlign: 'center' }}>
                  <FiDatabase size={40} style={{ color: '#334155', marginBottom: 12 }} />
                  <div style={{ color: '#64748B', fontSize: 16, fontWeight: 500 }}>
                    No documents loaded yet
                  </div>
                  <div style={{ color: '#475569', fontSize: 13, marginTop: 4 }}>
                    Upload documents or sync from SharePoint to get started
                  </div>
                </td>
              </tr>
            ) : (
              documents.map(doc => (
                <tr key={doc.source_file_id} style={{ borderBottom: '1px solid #1E2A4A' }}>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <FiFileText size={16} style={{ color: '#3B82F6', flexShrink: 0 }} />
                      <span style={{ color: '#E2E8F0', fontWeight: 500 }}>{doc.title}</span>
                    </div>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <span style={{
                      background: '#1E3A5F', color: '#60A5FA',
                      padding: '2px 10px', borderRadius: 12, fontSize: 13, fontWeight: 600,
                    }}>
                      {doc.chunk_count}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ color: '#94A3B8', fontSize: 13 }}>
                      {doc.last_ingested ? new Date(doc.last_ingested).toLocaleString() : '—'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      onClick={() => handleDelete(doc.source_file_id, doc.title)}
                      disabled={deleting === doc.source_file_id}
                      title="Delete all chunks"
                      style={{
                        background: 'none', border: 'none',
                        color: deleting === doc.source_file_id ? '#475569' : '#EF4444',
                        cursor: deleting === doc.source_file_id ? 'not-allowed' : 'pointer',
                        padding: 6, borderRadius: 6, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', margin: '0 auto',
                      }}
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Stats footer */}
      {!loading && documents.length > 0 && (
        <div style={{
          display: 'flex', gap: 24, marginTop: 16, color: '#64748B', fontSize: 13,
        }}>
          <span>{documents.length} document{documents.length !== 1 ? 's' : ''}</span>
          <span>{documents.reduce((sum, d) => sum + (d.chunk_count || 0), 0)} total chunks</span>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes fadeIn { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}

const thStyle = {
  padding: '12px 16px', textAlign: 'left',
  color: '#64748B', fontSize: 12, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.05em',
};

const tdStyle = {
  padding: '14px 16px',
};
